import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../config';

interface User {
  id: string;
  name: string;
  email: string;
  phoneNumber?: string;
  displayName?: string;
}

interface UserSelectorProps {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  multiple?: boolean;
}

const UserSelector: React.FC<UserSelectorProps> = ({
  value,
  onChange,
  onBlur,
  placeholder = "Select user...",
  className = "",
  style = {},
  multiple = false
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load users from database
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      const dbUsers: User[] = [];
      
      usersSnapshot.forEach((doc) => {
        const userData = doc.data();
        dbUsers.push({
          id: doc.id,
          name: userData.name || userData.displayName || 'Unknown User',
          email: userData.email || '',
          phoneNumber: userData.phoneNumber || '',
          displayName: userData.displayName || userData.name || 'Unknown User'
        });
      });
      
      setUsers(dbUsers);
      
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load users on mount
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) {
      return users;
    }
    
    const search = searchTerm.toLowerCase();
    return users.filter(user => {
      const name = user.name.toLowerCase();
      const email = user.email.toLowerCase();
      return name.includes(search) || email.includes(search);
    });
  }, [searchTerm, users]);

  // Initialize search term with current value when component mounts or value changes
  useEffect(() => {
    if (value && value !== searchTerm) {
      // For multiple selection, don't set searchTerm to the selected users
      // This would filter the list to only show selected users
      if (!multiple) {
        setSearchTerm(value);
      }
    }
  }, [value, searchTerm, multiple]);

  // Click outside to close dropdown (for fixed positioning)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        // Check if click is on backdrop
        const target = event.target as HTMLElement;
        if (target.style.background === 'rgba(0, 0, 0, 0.5)' || target.classList.contains('backdrop')) {
          // For multiple selection, only close on backdrop click, not on other clicks
          if (multiple) {
            console.log('👥 Multiple selection mode - closing on backdrop click only');
          }
          setIsOpen(false);
        }
      }
    };

    if (isOpen) {
      // Add a small delay to prevent immediate closing
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, multiple]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    
    // For multiple selection, don't clear selections when typing
    // The search is only for filtering the user list, not for editing selections
    if (!newValue.trim() && !multiple) {
      setSelectedUser(null);
      onChange('');
    }
    
    setIsOpen(true);
  }, [multiple, onChange]);

  const handleInputBlur = useCallback(() => {
    // For multiple selection, don't close on blur - let user click Done button
    if (multiple) {
      console.log('👥 Multiple selection mode - keeping modal open on blur');
      return;
    }
    
    // For single selection, close after a short delay
    setTimeout(() => {
      if (!document.activeElement || !dropdownRef.current?.contains(document.activeElement)) {
        setIsOpen(false);
        // Don't call onBlur here - it will be called by handleUserSelect
      }
    }, 300);
  }, [multiple]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'Backspace') {
      // Clear selection and show all users when backspace is pressed
      if (multiple) {
        setSelectedUsers([]);
        setSearchTerm('');
        onChange('');
      } else {
        setSelectedUser(null);
        setSearchTerm('');
        onChange('');
      }
    }
  }, [multiple, onChange]);

  // Global Escape key handler for modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleUserSelect = useCallback((user: User) => {
    if (multiple) {
      const isAlreadySelected = selectedUsers.some(u => u.id === user.id);
      let newSelectedUsers: User[];
      
      if (isAlreadySelected) {
        newSelectedUsers = selectedUsers.filter(u => u.id !== user.id);
      } else {
        newSelectedUsers = [...selectedUsers, user];
      }
      
      setSelectedUsers(newSelectedUsers);
      
      // For multiple selection, update the display value but DON'T update searchTerm
      // This keeps all users visible for selection
      const displayValue = newSelectedUsers.map(u => `${u.name} (${u.email})`).join(', ');
      onChange(displayValue);
      
      // IMPORTANT: Don't call onBlur immediately - let the user finish selecting
      // onBlur will be called when the modal is closed or Done button is clicked
      console.log(`🔔 onBlur NOT triggered yet - waiting for user to finish selection`);
      
      console.log(`👥 User ${isAlreadySelected ? 'deselected' : 'selected'}: ${user.name}`);
      console.log(`👥 Total selected: ${newSelectedUsers.length} users`);
      console.log(`👥 Selected users: ${displayValue}`);
      console.log(`🔔 onBlur triggered for notification sending`);
      
    } else {
      const displayValue = `${user.name} (${user.email})`;
      setSelectedUser(user);
      setSearchTerm(displayValue);
      onChange(displayValue);
      setIsOpen(false);
      onBlur();
    }
  }, [multiple, selectedUsers, onChange, onBlur]);

  const handleToggleDropdown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(prev => !prev);
  }, []);

  const handleInputFocus = useCallback((e: React.FocusEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(true);
  }, []);

  const handleInputClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(true);
  }, []);

  return (
    <div 
      className={`user-selector ${className}`} 
      style={{ 
        position: 'relative', 
        border: '2px solid #3b82f6',
        background: '#f8fafc',
        minHeight: '40px',
        borderRadius: '6px',
        ...style 
      }}
    >
      {/* Debug indicator */}
      <div style={{
        position: 'absolute',
        top: '-20px',
        left: '0',
        background: '#3b82f6',
        color: 'white',
        padding: '2px 6px',
        fontSize: '10px',
        zIndex: 1000,
        borderRadius: '3px'
      }}>
        UserSelector {isOpen ? '(Open)' : '(Closed)'}
      </div>
      
      <input
        ref={inputRef}
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        onClick={handleInputClick}
        placeholder={placeholder}
        className="user-selector-input"
        autoComplete="off"
        style={{
          width: '100%',
          height: '36px',
          padding: '8px 12px',
          border: 'none',
          outline: 'none',
          background: 'transparent',
          fontSize: '14px'
        }}
      />
      
      <button
        type="button"
        onClick={handleToggleDropdown}
        style={{
          position: 'absolute',
          right: '8px',
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '12px',
          color: '#6b7280',
          padding: '4px'
        }}
        title="Toggle user list"
      >
        {isOpen ? '▲' : '▼'}
      </button>
      
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="backdrop"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 9998
            }}
            onClick={() => {
              setIsOpen(false);
            }}
          />
          
          {/* Dropdown Modal */}
          <div 
            ref={dropdownRef} 
            className="user-selector-dropdown"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'white',
              border: '2px solid #3b82f6',
              borderRadius: '8px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              zIndex: 9999,
              maxHeight: '400px',
              width: '400px',
              overflow: 'hidden'
            }}
          >
          {/* Header */}
          <div style={{
            padding: '12px 16px', 
            background: '#f8fafc', 
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <strong style={{ color: '#1e40af', fontSize: '14px' }}>
                {multiple ? 'Select Multiple Users' : 'Select User'}
              </strong>
              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} found
                {multiple && selectedUsers.length > 0 && (
                  <span style={{ color: '#059669', fontWeight: '500' }}>
                    {' '}• {selectedUsers.length} selected
                  </span>
                )}
                {multiple && (
                  <div style={{ 
                    fontSize: '11px', 
                    color: '#6b7280', 
                    marginTop: '4px', 
                    fontStyle: 'italic',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <span>💡</span>
                    <span>Click checkboxes to select multiple users</span>
                    <span style={{ 
                      background: '#3b82f6', 
                      color: 'white', 
                      padding: '2px 6px', 
                      borderRadius: '10px',
                      fontSize: '10px',
                      fontWeight: 'bold'
                    }}>
                      MULTIPLE MODE
                    </span>
                  </div>
                )}
                {multiple && (
                  <div style={{ 
                    fontSize: '10px', 
                    color: '#059669', 
                    marginTop: '2px',
                    fontWeight: '500'
                  }}>
                    ✅ Modal stays open - select as many users as needed, then click "Done"
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                // Call onBlur after a small delay to ensure the value is properly set
                setTimeout(() => {
                  onBlur();
                }, 100);
              }}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '18px',
                cursor: 'pointer',
                color: '#64748b',
                padding: '4px',
                borderRadius: '4px'
              }}
              title="Close"
            >
              ✕
            </button>
          </div>
          
          {loading ? (
            <div style={{ padding: '20px', textAlign: 'center' }}>Loading users...</div>
          ) : error ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#dc2626' }}>
              <div>{error}</div>
              <button 
                type="button"
                onClick={() => loadUsers()}
                style={{
                  marginTop: '8px',
                  padding: '4px 8px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              {/* Search input */}
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}>
                <input
                  type="text"
                  placeholder={multiple ? "Search users to select..." : "Search users..."}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                  autoFocus
                />
                
                {/* Show selected users separately in multiple mode */}
                {multiple && selectedUsers.length > 0 && (
                  <div style={{
                    marginTop: '8px',
                    padding: '8px',
                    background: '#f0f9ff',
                    border: '1px solid #bae6fd',
                    borderRadius: '6px',
                    fontSize: '12px'
                  }}>
                    <div style={{ fontWeight: '500', color: '#0369a1', marginBottom: '4px' }}>
                      ✅ Selected Users ({selectedUsers.length}):
                    </div>
                    <div style={{ color: '#0c4a6e' }}>
                      {selectedUsers.map(u => u.name).join(', ')}
                    </div>
                  </div>
                )}
                
                {/* Select All / Clear All buttons for multiple selection */}
                {multiple && filteredUsers.length > 0 && (
                  <div style={{ 
                    display: 'flex', 
                    gap: '8px', 
                    marginTop: '8px',
                    justifyContent: 'center'
                  }}>
                    <button
                      type="button"
                      onClick={() => {
                        const allUsers = filteredUsers;
                        setSelectedUsers(allUsers);
                        const displayValue = allUsers.map(u => u.name).join(', ');
                        onChange(displayValue);
                        console.log(`👥 Select All clicked - selected ${allUsers.length} users`);
                      }}
                      style={{
                        padding: '4px 8px',
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      ✅ Select All ({filteredUsers.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedUsers([]);
                        onChange('');
                        console.log(`👥 Clear All clicked - cleared all selections`);
                      }}
                      style={{
                        padding: '4px 8px',
                        background: '#6b7280',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      🗑️ Clear All
                    </button>
                  </div>
                )}
              </div>

              {/* Users list */}
              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {filteredUsers.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                    No users found
                  </div>
                ) : (
                  filteredUsers.map((user, index) => {
                    const isSelected = multiple 
                      ? selectedUsers.some(u => u.id === user.id)
                      : selectedUser?.id === user.id;
                    
                    return (
                      <div
                        key={user.id || index}
                        onClick={(e) => {
                          // For multiple selection, only handle row click if checkbox wasn't clicked
                          if (multiple) {
                            // Don't handle row click in multiple mode - let checkbox handle it
                            return;
                          } else {
                            handleUserSelect(user);
                          }
                        }}
                        style={{ 
                          padding: '12px 16px', 
                          cursor: multiple ? 'default' : 'pointer', 
                          borderBottom: '1px solid #f3f4f6',
                          backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (!multiple && !isSelected) {
                            (e.target as HTMLElement).style.backgroundColor = '#f9fafb';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!multiple && !isSelected) {
                            (e.target as HTMLElement).style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                            {/* Checkbox for multiple selection */}
                            {multiple && (
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleUserSelect(user);
                                }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                style={{
                                  marginRight: '12px',
                                  width: '16px',
                                  height: '16px',
                                  accentColor: '#3b82f6',
                                  cursor: 'pointer'
                                }}
                              />
                            )}
                            <div style={{ flex: 1 }}>
                              <div style={{ 
                                fontWeight: '500', 
                                color: '#1f2937',
                                fontSize: '14px'
                              }}>
                                {user.name}
                              </div>
                              <div style={{ 
                                fontSize: '12px', 
                                color: '#6b7280',
                                marginTop: '2px'
                              }}>
                                {user.email}
                              </div>
                              {user.phoneNumber && (
                                <div style={{ 
                                  fontSize: '11px', 
                                  color: '#9ca3af',
                                  marginTop: '1px'
                                }}>
                                  {user.phoneNumber}
                                </div>
                              )}
                            </div>
                          </div>
                          {isSelected && !multiple && (
                            <div style={{ 
                              color: '#3b82f6',
                              fontSize: '16px',
                              fontWeight: 'bold'
                            }}>
                              ✓
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Done button for multiple selection */}
              {multiple && (
                <div style={{ 
                  padding: '12px 16px', 
                  borderTop: '1px solid #e2e8f0',
                  background: '#f8fafc'
                }}>
                  <button
                    type="button"
                    onClick={() => {
                      console.log(`👥 Done clicked - ${selectedUsers.length} users selected`);
                      setIsOpen(false);
                      // Call onBlur after a small delay to ensure the value is properly set
                      setTimeout(() => {
                        onBlur();
                      }, 100);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: selectedUsers.length > 0 ? '#3b82f6' : '#d1d5db',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: selectedUsers.length > 0 ? 'pointer' : 'not-allowed',
                      fontSize: '14px',
                      fontWeight: '600',
                      boxShadow: selectedUsers.length > 0 ? '0 2px 4px rgba(59, 130, 246, 0.3)' : 'none',
                      transition: 'all 0.2s ease'
                    }}
                    disabled={selectedUsers.length === 0}
                  >
                    {selectedUsers.length > 0 ? (
                      `✅ Done (${selectedUsers.length} selected)`
                    ) : (
                      'Select users to continue'
                    )}
                  </button>
                  
                  {selectedUsers.length > 0 && (
                    <div style={{
                      fontSize: '12px',
                      color: '#059669',
                      textAlign: 'center',
                      marginTop: '8px',
                      fontWeight: '500'
                    }}>
                      💡 Click Done when finished selecting users
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          </div>
        </>
      )}
    </div>
  );
};

export default UserSelector;